import { TOURNAMENT_ROLES, TOURNAMENT_STATUSES } from '@/constants';
import type { TournamentRole, TournamentStatus } from '@/constants';
import type { TournamentListSource } from '@/types';

/** Tab path of each tournament list. */
export const TOURNAMENT_SOURCE_PATH: Record<TournamentListSource, string> = {
  my: '/tournaments',
  public: '/tournaments/public',
  archived: '/tournaments/archived',
};

/** Tournament list tab of a pathname (anything outside public / archived is "my"). */
export function tournamentSourceFromPath(pathname: string): TournamentListSource {
  if (pathname.startsWith(TOURNAMENT_SOURCE_PATH.archived)) return 'archived';
  if (pathname.startsWith(TOURNAMENT_SOURCE_PATH.public)) return 'public';
  return 'my';
}

export const TOURNAMENT_PAGE_SIZES = [10, 20, 50] as const;
export const DEFAULT_TOURNAMENT_PAGE_SIZE = TOURNAMENT_PAGE_SIZES[0];

/** URL search params owned by the tournament list filters. */
export const TOURNAMENT_LIST_PARAM = {
  QUERY: 'q',
  STATUS: 'status',
  SEASON: 'season',
  ROLE: 'role',
  PAGE: 'page',
  SIZE: 'size',
} as const;

export type TournamentListParamName = (typeof TOURNAMENT_LIST_PARAM)[keyof typeof TOURNAMENT_LIST_PARAM];

const STATUS_VALUES = Object.values(TOURNAMENT_STATUSES) as string[];
const ROLE_VALUES = Object.values(TOURNAMENT_ROLES) as string[];
const SEASON_PATTERN = /^\d{4}$/;
const SIZE_VALUES = TOURNAMENT_PAGE_SIZES as readonly number[];

export interface ParsedTournamentListParams {
  /** Search text as stored in the URL. */
  q: string;
  status: TournamentStatus | null;
  /** Four-digit season year, or null for all seasons. */
  season: string | null;
  role: TournamentRole | null;
  /** 0-based page index (the URL keeps it 1-based). */
  page: number;
  pageSize: number;
}

/** Validated tournament list filters from URL search params; invalid values fall back to defaults. */
export function parseTournamentListParams(params: URLSearchParams): ParsedTournamentListParams {
  const rawStatus = params.get(TOURNAMENT_LIST_PARAM.STATUS) ?? '';
  const rawSeason = params.get(TOURNAMENT_LIST_PARAM.SEASON) ?? '';
  const rawRole = params.get(TOURNAMENT_LIST_PARAM.ROLE) ?? '';
  const rawPage = Number(params.get(TOURNAMENT_LIST_PARAM.PAGE));
  const rawSize = Number(params.get(TOURNAMENT_LIST_PARAM.SIZE));

  return {
    q: params.get(TOURNAMENT_LIST_PARAM.QUERY) ?? '',
    status: STATUS_VALUES.includes(rawStatus) ? (rawStatus as TournamentStatus) : null,
    season: SEASON_PATTERN.test(rawSeason) ? rawSeason : null,
    role: ROLE_VALUES.includes(rawRole) ? (rawRole as TournamentRole) : null,
    page: Number.isInteger(rawPage) && rawPage > 1 ? rawPage - 1 : 0,
    pageSize: SIZE_VALUES.includes(rawSize) ? rawSize : DEFAULT_TOURNAMENT_PAGE_SIZE,
  };
}
